import { Input, theme } from 'antd'
import type { InputProps } from 'antd'
import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { useDebouncedValue, useQueryParams, useQueryUpdate } from 'src/hooks'
import { defaultFilters } from 'src/utils'

const config = {
  queryParam: 'search',
  iconSize: 14,
  width: FILTER_CONTROL_WIDTH
} as const

type Props = {
  queryParam?: string
} & InputProps

export const SearchInput = ({
  queryParam = config.queryParam,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()
  const queryValue = Array.isArray(params?.[queryParam])
    ? String(params[queryParam][0] ?? '')
    : String(params?.[queryParam] ?? '')

  const [searchValue, setSearchValue] = useState(queryValue)

  const debouncedSearch = useDebouncedValue(searchValue)

  useEffect(() => {
    setSearchValue(queryValue)
  }, [queryValue])

  useEffect(() => {
    if (queryValue === debouncedSearch) {
      return
    }

    updateQuery({
      ...params,
      page: defaultFilters.page,
      [queryParam]: debouncedSearch
    })
  }, [debouncedSearch])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    props?.onChange?.(e)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    props.onKeyDown?.(e)
    if (e.key !== 'Enter') {
      return
    }

    e.stopPropagation()
    e.preventDefault()

    updateQuery({
      ...params,
      page: defaultFilters.page,
      [queryParam]: searchValue
    })
  }

  return (
    <Input
      {...props}
      style={{ width: config.width, ...props.style }}
      value={searchValue}
      prefix={<Search size={config.iconSize} color={token.colorTextTertiary} />}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={props.placeholder || t('search')}
      allowClear
    />
  )
}
